// Detail pane → Overview. Replaces the card body and its reminders panel.

import {
  Badge,
  Box,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Chatbot } from '@/lib/sdk/chatbot/types';

const REMINDER_LABELS: Record<string, { label: string; colorScheme: string }> = {
  send_task_reminder: { label: 'Task reminder', colorScheme: 'blue' },
  send_summary_reminder: { label: 'Summary', colorScheme: 'purple' },
};

function reminderLabel(nudgeType: string) {
  return (
    REMINDER_LABELS[nudgeType] ?? {
      label: nudgeType?.replace(/_/g, ' ') ?? 'Reminder',
      colorScheme: 'gray',
    }
  );
}

export function OverviewTab({ chatbot }: { chatbot: Chatbot }) {
  const reminders = chatbot.reminders ?? [];

  return (
    <Box data-testid="wfl-overview-panel">
      <SimpleGrid columns={3} spacing={4} mb={6}>
        <Stat bg="bg-muted" borderRadius="md" p={4}>
          <StatLabel color="text-secondary" fontSize="xs">Tasks</StatLabel>
          <StatNumber fontSize="2xl" data-testid="wfl-overview-task-count">
            {chatbot.task_count}
          </StatNumber>
        </Stat>
        <Stat bg="bg-muted" borderRadius="md" p={4}>
          <StatLabel color="text-secondary" fontSize="xs">Team members</StatLabel>
          <StatNumber fontSize="2xl">{chatbot.team?.length ?? 0}</StatNumber>
        </Stat>
        <Stat bg="bg-muted" borderRadius="md" p={4}>
          <StatLabel color="text-secondary" fontSize="xs">Reminders</StatLabel>
          <StatNumber fontSize="2xl">{reminders.length}</StatNumber>
        </Stat>
      </SimpleGrid>

      <Text
        fontSize="xs"
        textTransform="uppercase"
        letterSpacing="wider"
        color="text-secondary"
        mb={3}
      >
        Reminders
      </Text>
      {reminders.length === 0 ? (
        <Text fontSize="sm" color="text-secondary">
          No reminders configured for this workflow.
        </Text>
      ) : (
        <VStack align="stretch" spacing={2} maxW="420px">
          {reminders.map((reminder) => {
            const meta = reminderLabel(reminder.nudge_type);
            return (
              <HStack
                key={reminder.id}
                justify="space-between"
                borderWidth="1px"
                borderColor="border-default"
                borderRadius="md"
                px={3}
                py={2}
              >
                <Text fontSize="sm" color="text-primary">{reminder.nudge_time}</Text>
                <Badge colorScheme={meta.colorScheme}>{meta.label}</Badge>
              </HStack>
            );
          })}
        </VStack>
      )}
    </Box>
  );
}
